<template>
    <div class="apprentice-simple-card group-list">
        <design-2021-apprentice-data-table
            class="apprentice-group-table"
            v-if="items.length"
            :fields="fields"
            :items="items"
            :hide-columns="hideColumns">
            <template v-slot:options>
                <div class="seasons">
                    <label for="season-select">Schooljaar:</label>
                    <b-form-select
                        class="apprentice-select group-year-select"
                        id="season-select"
                        v-if="seasons.length"
                        @change="seasonChanged"
                        :value="season"
                        :options="seasons"
                        text-field="season"
                        value-field="id">
                    </b-form-select>
                </div>
            </template>

            <template v-slot:cell(actions)="data">
                <div class="group-actions">
                    <b-button
                        class="btn-recycle btn-open-group remove-icon-margin"
                        v-if="!data.item.parent_also_deleted"
                        v-b-tooltip="'Project terugzetten'"
                        variant="primary-icon-only"
                        @click="restore(data.item)">
                    </b-button>
                </div>
            </template>

            <template v-slot:cell(group_name)="row">
                <div
                    :class="row.item.parent_id ? 'sibling' : 'parent'"
                    :style="`padding-left: ${row.item.level * 16}px`">
                    {{ row.item.group_name }}
                </div>
            </template>
        </design-2021-apprentice-data-table>
        <div
            class="not-found with-header removed-groups-not-found"
            v-else>
            <div class="btn-recycle icon"></div>
            <div class="alert-notice">Er zijn geen verwijderde projecten gevonden.</div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'design-2021-group-restore-component',
    props: {
        groupsIn: Array,
        courses: Array,
        seasonsIn: Array,
        currentSeasonId: Number,
    },
    data() {
        return {
            groups: this.groupsIn,
            hideColumns: ['actions', 'start_end_date'],
            seasons: [],
            season: 0,
        };
    },

    mounted() {
        let seasonWithGroups = this.seasonsIn
            .filter((season) => this.groups.findIndex((group) => group.season_id === season.id) !== -1)
            .map((season) => {
                if (season.id !== this.currentSeasonId) {
                    return season;
                }
                let currentSeason = { ...season };
                currentSeason.season = 'Huidig schooljaar';
                return currentSeason;
            });

        if (seasonWithGroups.length) {
            seasonWithGroups.push({
                id: 0,
                season: 'Alle projecten',
            });
            this.seasons = seasonWithGroups.reverse();

            const key = 'group-overview-season-' + this.$authUser.id;
            const seasonStored = parseInt(localStorage.getItem(key));
            if (seasonStored && this.seasonsIn.some((season) => season.id === seasonStored)) {
                this.season = seasonStored;
            } else if (this.currentSeasonId && this.seasonsIn.some((season) => season.id === this.currentSeasonId)) {
                this.season = this.currentSeasonId;
            }
        }
    },

    methods: {
        seasonChanged(value) {
            const key = 'group-overview-season-' + this.$authUser.id;
            localStorage.setItem(key, value);
            this.season = value;
        },

        restore(restoreGroup) {
            this.alert
                .confirm(
                    'Project terugzetten',
                    'Weet je zeker dat je dit project, alle deelprojecten en alle werkstukken wil terugzetten?',
                    'ja terugzetten',
                )
                .then((confirm) => {
                    if (confirm) {
                        this.alert.saving();

                        this.axios.post(this.routes.groupRestore(restoreGroup.group_id)).then(
                            (response) => {
                                this.groups = this.groups.filter((group) => !response.data.includes(group.id));
                                this.alert.success();
                            },
                            (error) => {
                                return this.alert.ajaxError(error);
                            },
                        );
                    }
                });
        },
    },

    computed: {
        fields() {
            let fields = [
                {
                    key: 'group_name',
                    label: this.$t('common.name'),
                    sortable: true,
                },
                {
                    key: 'parent_name',
                    label: 'Hoofdgroep',
                    sortable: true,
                },
                {
                    key: 'course_title',
                    label: 'Vak',
                    sortable: true,
                },
                {
                    key: 'start_end_date',
                    label: 'Start | Eind',
                    sortable: true,
                },
            ];
            if (this.season === 0) {
                fields.push({
                    key: 'season_year',
                    label: 'Schooljaar',
                    sortable: true,
                });
            }
            fields.push({
                key: 'actions',
                label: this.$t('common.actions'),
            });
            return fields;
        },
        items() {
            let items = [];

            if (this.groups.length) {
                items = this.groups.map((group) => {
                    let seasonYear = '';
                    let courseTitle = '';

                    let seasonIndex = this.seasons.findIndex((season) => season.id === group.season_id);
                    if (seasonIndex >= 0) {
                        seasonYear = this.seasons[seasonIndex].season;
                    }

                    let courseIndex = this.courses.findIndex((course) => course.id === group.course_id);
                    if (courseIndex >= 0) {
                        courseTitle = this.courses[courseIndex].name;
                    }

                    return {
                        group_id: group.id,
                        group_name: group.name,
                        course_id: group.course_id,
                        course_title: courseTitle,
                        start_end_date: `${group.start_date} | ${group.end_date}`,
                        start_date: group.start_date,
                        start: group.start,
                        end: group.end,
                        end_date: group.end_date,
                        season_id: group.season_id,
                        season_year: seasonYear,
                        parent_id: group.parent_id,
                        level: group.level,
                        type_id: group.type_id,
                        parent_name: group.parent_id ? group.parent.name : '',
                        parent_also_deleted: group.parent_id
                            ? !!this.groups.find((group0) => group0.id === group.parent_id)
                            : false,
                    };
                });

                if (this.season !== 0) {
                    items = items.filter((item) => {
                        if (item.season_id === this.season) {
                            return true;
                        }
                    });
                }
            }

            return items;
        },
    },
};
</script>

<style scoped lang="scss">
.apprentice-group-table ::v-deep {
    padding: 0;
}

.apprentice-data-table-actions {
    .btn {
        width: 100%;
    }
}

.btn-open-group {
    align-self: center;
}

.seasons {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    margin-right: 0;

    label {
        margin-bottom: 0;
        margin-right: map-get($grid-gutter-spacing, 'eight');
    }

    .group-year-select {
        min-width: 160px;
    }
}

.group-actions {
    display: flex;

    .btn {
        font-size: 1.125rem;

        &:not(:last-child) {
            margin-right: map-get($grid-gutter-spacing, 'twenty-one');
        }
    }

    ::v-deep button.project-context-menu {
        font-size: $base-font-size !important;
        padding: map-get($grid-gutter-spacing, 'five');

        &,
        &:hover,
        &:not(:disabled):not(.disabled):active,
        &:focus,
        &:focus-visible {
            background-color: $primary;
            color: $white !important;
        }
    }
}

.sibling {
    padding-left: 16px !important;

    & + .sibling {
        padding-left: 32px !important;

        & + .sibling {
            padding-left: 48px !important;
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'sm')) {
    .seasons {
        margin-right: map-get($grid-gutter-spacing, 'twenty-one');
    }

    .apprentice-data-table-actions {
        .btn {
            width: auto;
        }
    }
}
</style>
